import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { NavProvider } from "@/context/NavContext";
import { Seo } from "@/components/Seo";
import NavGuard from "@/components/NavGuard";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import Pricing from "@/components/Pricing";
import Order from "@/components/Order";
import Manifesto from "@/components/Manifesto";
import Tracking from "@/components/Tracking";
import InquiryForm from "@/components/InquiryForm";
import QuoteAcceptance from "@/components/QuoteAcceptance";
import BlogList from "@/pages/BlogList";
import BlogPost from "@/pages/BlogPost";
import CmsPage from "@/pages/CmsPage";
import NotFound from "@/pages/NotFound";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import ProtectionPlan from "@/components/ProtectionPlan";
import ProtectionSignup from "@/pages/ProtectionSignup";
import ProtectionSuccess from "@/pages/ProtectionSuccess";
import ProtectionPaymentCancel from "@/pages/ProtectionPaymentCancel";
import ProtectionRepairRequest from "@/pages/ProtectionRepairRequest";
import ProtectionMine from "@/pages/ProtectionMine";
import Legal from "@/pages/Legal";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import ProtectedRoute from "@/components/ProtectedRoute";

const Section = ({ title, description, children }) => (
  <div className="pt-24">
    {title && <Seo title={title} description={description} />}
    {children}
  </div>
);

const LEGAL_TITLES = {
  impressum: "G-Repair — Impressum",
  agb: "G-Repair — AGB",
  widerruf: "G-Repair — Widerrufsrecht",
  cookies: "G-Repair — Cookie-Hinweise",
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <NavProvider>
        <Toaster theme="dark" position="top-center" richColors />
        <Routes>
          <Route element={<Layout />}>
            <Route path="/" element={<><Seo title="G-Repair — iPhone Reparatur Express, günstig & sicher" description="iPhone-Reparatur deutschlandweit per Einsendeservice: faire Festpreise, Express in 1 Werktag, 100% Datensicherheit. Jetzt online beauftragen!" /><Home /></>} />
            <Route path="/beauftragen" element={<Section title="G-Repair — Reparatur beauftragen" description="Wähle dein iPhone-Modell, sieh den Festpreis für Display oder Akku und beauftrage deine Reparatur per Einsendeservice inkl. Online-Zahlung."><Order /></Section>} />
            <Route path="/preisliste" element={<NavGuard navKey="preisliste"><Section title="G-Repair — Preise" description="Faire Festpreise für Display-Reparatur und Akkutausch je iPhone-Modell, inklusive Einbau, zzgl. 6,95 € Versand deutschlandweit."><Pricing /></Section></NavGuard>} />
            <Route path="/ablauf" element={<NavGuard navKey="ablauf"><Section title="G-Repair — So funktioniert's" description="Vom Einsenden bis zur Rückgabe: So läuft deine iPhone-Reparatur per Einsendeservice bei G-Repair in 4 einfachen Schritten ab."><Manifesto /></Section></NavGuard>} />
            <Route path="/verfolgen" element={<NavGuard navKey="verfolgen"><Section title="G-Repair — Reparatur verfolgen" description="Verfolge den Status deiner iPhone-Reparatur mit deiner Referenznummer – von eingegangen bis repariert & zurückgesendet."><Tracking /></Section></NavGuard>} />
            <Route path="/anfrage" element={<NavGuard navKey="anfrage"><Section title="G-Repair — Individuelle Anfrage" description="Ladebuchse, Kamera, Wasserschaden oder etwas ganz anderes: Beschreib dein Anliegen und erhalte ein individuelles Angebot per E-Mail."><InquiryForm /></Section></NavGuard>} />
            <Route path="/ratgeber" element={<NavGuard navKey="ratgeber"><Section title="G-Repair — Ratgeber" description="Tipps, Anleitungen und Wissenswertes rund um iPhone-Reparaturen von G-Repair."><BlogList /></Section></NavGuard>} />
            <Route path="/ratgeber/:slug" element={<NavGuard navKey="ratgeber"><Section><BlogPost /></Section></NavGuard>} />
            <Route path="/schutzpaket" element={<NavGuard navKey="schutzpaket"><Section title="G-Repair — Schutzpaket" description="Jedes Jahr eine iPhone-Reparatur ohne Zusatzkosten: das G-Repair Schutzpaket als monatlicher Servicevertrag."><ProtectionPlan /></Section></NavGuard>} />
            <Route path="/schutzpaket/abschliessen" element={<NavGuard navKey="schutzpaket"><Section title="G-Repair — Schutzpaket abschließen"><ProtectionSignup /></Section></NavGuard>} />
            <Route path="/schutzpaket/erfolg" element={<NavGuard navKey="schutzpaket"><Section title="G-Repair — Schutzpaket aktiv"><ProtectionSuccess /></Section></NavGuard>} />
            <Route path="/schutzpaket/zahlung-abgebrochen" element={<NavGuard navKey="schutzpaket"><Section title="G-Repair — Zahlung abgebrochen"><ProtectionPaymentCancel /></Section></NavGuard>} />
            <Route path="/schutzpaket/reparatur" element={<NavGuard navKey="schutzpaket"><Section title="G-Repair — Schutzpaket Reparatur"><ProtectionRepairRequest /></Section></NavGuard>} />
            <Route path="/schutzpaket/meins" element={<NavGuard navKey="schutzpaket"><Section title="G-Repair — Mein Schutzpaket"><ProtectionMine /></Section></NavGuard>} />
            <Route path="/angebot/:token" element={<Section><QuoteAcceptance /></Section>} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/impressum" element={<Section title={LEGAL_TITLES.impressum}><Legal type="impressum" /></Section>} />
            <Route path="/agb" element={<Section title={LEGAL_TITLES.agb}><Legal type="agb" /></Section>} />
            <Route path="/widerruf" element={<Section title={LEGAL_TITLES.widerruf}><Legal type="widerruf" /></Section>} />
            <Route path="/cookies" element={<Section title={LEGAL_TITLES.cookies}><Legal type="cookies" /></Section>} />
            <Route path="/:slug" element={<Section><CmsPage /></Section>} />
            <Route path="*" element={<Section><NotFound /></Section>} />
          </Route>
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        </Routes>
      </NavProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;

